export interface ActionWording {
  readonly title: string;
  readonly running: string;
  readonly completed: string;
  readonly hintKeys?: ReadonlyArray<string>;
}

const CONNECTOR_NAMES: Readonly<Record<string, string>> = {
  airtable: "Airtable",
  altruist: "Altruist",
  asana: "Asana",
  attio: "Attio",
  box: "Box",
  clickup: "ClickUp",
  confluence: "Confluence",
  dropbox: "Dropbox",
  github: "GitHub",
  gmail: "Gmail",
  googlecalendar: "Google Calendar",
  googledocs: "Google Docs",
  googledrive: "Google Drive",
  googlesheets: "Google Sheets",
  googleslides: "Google Slides",
  hubspot: "HubSpot",
  intercom: "Intercom",
  jira: "Jira",
  laserfiche: "Laserfiche",
  linear: "Linear",
  microsoft_teams: "Microsoft Teams",
  monday: "monday.com",
  notion: "Notion",
  onedrive: "OneDrive",
  outlook: "Outlook",
  plainsail: "PlainSail",
  ruleguard: "RuleGuard",
  salesforce: "Salesforce",
  sharepoint: "SharePoint",
  slack: "Slack",
  trello: "Trello",
  zendesk: "Zendesk",
};

/** Curated wording for the actions people actually see, keyed by slug. */
const ACTIONS: Readonly<Record<string, ActionWording>> = {
  GMAIL_FETCH_EMAILS: {
    title: "search emails",
    running: "Searching emails",
    completed: "Searched emails",
    hintKeys: ["query"],
  },
  GMAIL_FETCH_MESSAGE_BY_MESSAGE_ID: {
    title: "read email",
    running: "Reading email",
    completed: "Read email",
  },
  GMAIL_FETCH_MESSAGE_BY_THREAD_ID: {
    title: "read email thread",
    running: "Reading email thread",
    completed: "Read email thread",
  },
  GMAIL_SEND_EMAIL: {
    title: "send email",
    running: "Sending email",
    completed: "Sent email",
    hintKeys: ["recipient_email", "to", "subject"],
  },
  GMAIL_CREATE_EMAIL_DRAFT: {
    title: "draft email",
    running: "Drafting email",
    completed: "Drafted email",
    hintKeys: ["recipient_email", "to", "subject"],
  },
  GMAIL_REPLY_TO_THREAD: {
    title: "reply to email",
    running: "Replying to email",
    completed: "Replied to email",
    hintKeys: ["recipient_email", "to"],
  },
  GOOGLEDRIVE_FIND_FILE: {
    title: "search Drive files",
    running: "Searching Drive files",
    completed: "Searched Drive files",
    hintKeys: ["query", "file_name"],
  },
  GOOGLEDRIVE_DOWNLOAD_FILE: {
    title: "read Drive file",
    running: "Reading Drive file",
    completed: "Read Drive file",
    hintKeys: ["file_name"],
  },
  GOOGLEDRIVE_UPLOAD_FILE: {
    title: "upload file to Drive",
    running: "Uploading file to Drive",
    completed: "Uploaded file to Drive",
    hintKeys: ["file_name"],
  },
  GOOGLECALENDAR_FIND_EVENT: {
    title: "search calendar events",
    running: "Searching calendar events",
    completed: "Searched calendar events",
    hintKeys: ["query"],
  },
  GOOGLECALENDAR_CREATE_EVENT: {
    title: "create calendar event",
    running: "Creating calendar event",
    completed: "Created calendar event",
    hintKeys: ["summary", "title"],
  },
  SLACK_SEND_MESSAGE: {
    title: "send Slack message",
    running: "Sending Slack message",
    completed: "Sent Slack message",
    hintKeys: ["channel"],
  },
  SLACK_SENDS_A_MESSAGE_TO_A_SLACK_CHANNEL: {
    title: "send Slack message",
    running: "Sending Slack message",
    completed: "Sent Slack message",
    hintKeys: ["channel"],
  },
  SLACK_SEARCH_MESSAGES: {
    title: "search Slack messages",
    running: "Searching Slack messages",
    completed: "Searched Slack messages",
    hintKeys: ["query"],
  },
  NOTION_SEARCH_NOTION_PAGE: {
    title: "search Notion pages",
    running: "Searching Notion pages",
    completed: "Searched Notion pages",
    hintKeys: ["query"],
  },
};

/** Verb → [present participle, past tense], for slugs without curated wording. */
const VERBS: Readonly<Record<string, readonly [string, string]>> = {
  add: ["Adding", "Added"],
  append: ["Appending", "Appended"],
  archive: ["Archiving", "Archived"],
  attach: ["Attaching", "Attached"],
  check: ["Checking", "Checked"],
  copy: ["Copying", "Copied"],
  create: ["Creating", "Created"],
  decide: ["Deciding", "Decided"],
  delete: ["Deleting", "Deleted"],
  download: ["Downloading", "Downloaded"],
  draft: ["Drafting", "Drafted"],
  edit: ["Editing", "Edited"],
  execute: ["Running", "Ran"],
  fetch: ["Fetching", "Fetched"],
  find: ["Finding", "Found"],
  get: ["Reading", "Read"],
  insert: ["Inserting", "Inserted"],
  invite: ["Inviting", "Invited"],
  link: ["Linking", "Linked"],
  list: ["Listing", "Listed"],
  lookup: ["Looking up", "Looked up"],
  mark: ["Marking", "Marked"],
  modify: ["Modifying", "Modified"],
  move: ["Moving", "Moved"],
  patch: ["Updating", "Updated"],
  post: ["Posting", "Posted"],
  propose: ["Proposing", "Proposed"],
  query: ["Querying", "Queried"],
  read: ["Reading", "Read"],
  reclassify: ["Reclassifying", "Reclassified"],
  record: ["Recording", "Recorded"],
  remove: ["Removing", "Removed"],
  reply: ["Replying", "Replied"],
  retrieve: ["Retrieving", "Retrieved"],
  revise: ["Revising", "Revised"],
  run: ["Running", "Ran"],
  schedule: ["Scheduling", "Scheduled"],
  search: ["Searching", "Searched"],
  send: ["Sending", "Sent"],
  set: ["Setting", "Set"],
  share: ["Sharing", "Shared"],
  toggle: ["Toggling", "Toggled"],
  update: ["Updating", "Updated"],
  upload: ["Uploading", "Uploaded"],
  withdraw: ["Withdrawing", "Withdrew"],
};

const compactKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, "");

export function connectorDisplayName(connectorId: string): string {
  const key = connectorId.toLowerCase().trim();
  const known = CONNECTOR_NAMES[key] ?? CONNECTOR_NAMES[key.replace(/[\s-]+/g, "_")];
  if (known) return known;
  const words = key.split(/[_\s-]+/).filter((word) => word.length > 0);
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") || "app";
}

/**
 * Wording read off the slug: drop the connector prefix, treat the next word
 * as the verb and the rest as its object. `GMAIL_LIST_DRAFTS` → "Listing
 * drafts" / "Listed drafts". Composio also names a few actions in the third
 * person (`SLACK_SENDS_A_MESSAGE…`); a trailing `s` is tried before giving up.
 */
function wordingFromSlug(slug: string, connectorId: string, connectorName: string): ActionWording {
  const fallback: ActionWording = {
    title: `use ${connectorName}`,
    running: `Using ${connectorName}`,
    completed: `Used ${connectorName}`,
  };
  const words = slug
    .toLowerCase()
    .split("_")
    .filter((word) => word.length > 0);
  const connectorKey = compactKey(connectorId);
  let prefix = "";
  let index = 0;
  while (index < words.length && connectorKey.startsWith(prefix + words[index]!)) {
    prefix += words[index]!;
    index += 1;
    if (prefix === connectorKey) break;
  }
  if (prefix !== connectorKey) index = 0;
  return (
    verbPhraseWording(words.slice(index)) ??
    verbOnlyWording(words[index], connectorName) ??
    fallback
  );
}

function knownVerb(word: string | undefined): string | null {
  if (!word) return null;
  if (Object.hasOwn(VERBS, word)) return word;
  return word.endsWith("s") && Object.hasOwn(VERBS, word.slice(0, -1)) ? word.slice(0, -1) : null;
}

function verbForms(verb: string): { infinitive: string; running: string; completed: string } {
  const [running, completed] = VERBS[verb]!;
  // "get" reads as "read" and "execute" as "run" ("Failed to read policy").
  const infinitive = verb === "get" || verb === "execute" ? completed.toLowerCase() : verb;
  return { infinitive, running, completed };
}

function verbOnlyWording(word: string | undefined, connectorName: string): ActionWording | null {
  const verb = knownVerb(word);
  if (verb === null) return null;
  const { infinitive, running, completed } = verbForms(verb);
  return {
    title: `${infinitive} in ${connectorName}`,
    running: `${running} in ${connectorName}`,
    completed: `${completed} in ${connectorName}`,
  };
}

/**
 * Wording for a verb-first phrase split into lower-case words
 * (`["list", "policies"]` → "list policies" / "Listing policies" / "Listed
 * policies"). Null when the first word is not a known verb or nothing
 * follows it, so the caller can fall back to its own wording.
 */
export function verbPhraseWording(words: ReadonlyArray<string>): ActionWording | null {
  const verb = knownVerb(words[0]);
  const object = words.slice(1).join(" ");
  if (verb === null || object.length === 0) return null;
  const { infinitive, running, completed } = verbForms(verb);
  return {
    title: `${infinitive} ${object}`,
    running: `${running} ${object}`,
    completed: `${completed} ${object}`,
  };
}

export function connectorActionWording(slug: string, connectorId: string): ActionWording {
  return (
    ACTIONS[slug.toUpperCase()] ??
    wordingFromSlug(slug, connectorId, connectorDisplayName(connectorId))
  );
}
